import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { supabase } from '@/lib/supabase';
import { formatDateTime } from '@/lib/format';

type Feature = 'briefing' | 'recommend' | 'lost_analysis';

interface AiResponse {
  status: 'ok' | 'unavailable' | 'budget_exhausted' | 'error';
  cached?: boolean;
  text?: string;
  model?: string;
  generated_at?: string;
  reason?: string;
  message?: string;
}

/**
 * Gemini output, shown as advice and never as data (#37, #38).
 *
 * The acceptance criterion is "no AI output is ever written to a business field
 * without human confirmation", and the strongest way to satisfy it is to have no
 * write path at all: this panel renders text and offers a copy button. Nothing
 * here can set a stage, a budget, or a lost reason. If a rep wants to act on a
 * suggestion they do it through the normal form, where the usual validation and
 * audit apply.
 *
 * It is also visibly labelled. A manager reading a briefing needs to know which
 * sentences came from a model and which came from the database — the numbers on
 * the dashboard above are facts, this is a summary of them.
 */
export function AiPanel({
  feature,
  leadId,
  title,
}: {
  feature: Feature;
  leadId?: string | undefined;
  title: string;
}) {
  const { t } = useTranslation();
  const [result, setResult] = useState<AiResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const ask = useMutation({
    mutationFn: async (): Promise<AiResponse> => {
      // functions.invoke is typed as any; narrow once, here, so AiResponse above
      // stays the only description of what comes back.
      const result = (await supabase.functions.invoke('ai-assist', {
        body: { feature, lead_id: leadId },
      })) as { data: unknown; error: { message?: string } | null };

      if (result.error) throw new Error(result.error.message ?? 'ai_invoke_failed');
      return result.data as AiResponse;
    },
    onSuccess: (data) => setResult(data),
  });

  const copy = async () => {
    if (!result?.text) return;
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked in some in-app browsers; the text is on screen and
      // selectable either way, so this is not worth an error message.
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Sparkles className="size-4 text-brand-600" aria-hidden="true" />
          {title}
        </h2>
        <Button variant="secondary" loading={ask.isPending} onClick={() => ask.mutate()}>
          {result ? t('ai.regenerate') : t('ai.generate')}
        </Button>
      </div>

      {ask.isPending ? <Spinner /> : null}

      {ask.isError ? <Alert tone="error">{t('ai.failed')}</Alert> : null}

      {/* Not an error: the whole stack is meant to work without a Gemini key. */}
      {result?.status === 'unavailable' ? <Alert tone="info">{t('ai.notConfigured')}</Alert> : null}

      {result?.status === 'budget_exhausted' ? (
        <Alert tone="info">{t('ai.budgetExhausted')}</Alert>
      ) : null}

      {result?.status === 'error' ? <Alert tone="error">{t('ai.failed')}</Alert> : null}

      {result?.status === 'ok' && result.text ? (
        <div className="flex flex-col gap-2">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
            {result.text}
          </p>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
            <p className="text-xs text-slate-400">
              {t('ai.disclaimer')}
              {result.generated_at ? ` · ${formatDateTime(result.generated_at)}` : ''}
              {result.cached ? ` · ${t('ai.fromCache')}` : ''}
            </p>
            <Button variant="ghost" onClick={() => void copy()}>
              {copied ? t('ai.copied') : t('ai.copy')}
            </Button>
          </div>
        </div>
      ) : null}

      {!result && !ask.isPending ? (
        <p className="text-xs text-slate-500">{t(`ai.hints.${feature}` as 'ai.hints.briefing')}</p>
      ) : null}
    </div>
  );
}
